/**
 * Pack 10 · Admin Plan Management Dialog.
 *
 * One-shot dialog fired from the Admin consultants table. Consumes:
 *   GET  /api/admin/consultants/{cid}/billing/preview
 *   POST /api/admin/consultants/{cid}/billing/change
 *   GET  /api/admin/consultants/{cid}/billing/audit
 *
 * NEVER touches Paddle directly. All state lives in Mongo.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Switch } from "./ui/switch";
import { Card } from "./ui/card";
import { CalendarClock, ChevronRight, History, Loader2, ScrollText } from "lucide-react";

const currency = (n) => {
  if (typeof n !== "number") return "—";
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.abs(n).toFixed(2)}`;
};

const actionChip = (a) => {
  switch (a) {
    case "upgrade": return "bg-emerald-500/15 text-emerald-300 border-emerald-500/40";
    case "downgrade": return "bg-red-500/15 text-red-300 border-red-500/40";
    case "same_tier_change": return "bg-amber-500/15 text-amber-300 border-amber-500/40";
    default: return "bg-secondary/40 text-muted-foreground border-border/60";
  }
};

export default function PlanManagementDialog({ consultant, open, onOpenChange, packages }) {
  const [targetPackageId, setTargetPackageId] = useState("");
  const [forceImmediate, setForceImmediate] = useState(false);
  const [preview, setPreview] = useState(null);
  const [audit, setAudit] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [applying, setApplying] = useState(false);

  const packageOptions = useMemo(() => Object.entries(packages || {}), [packages]);

  const runPreview = useCallback(async () => {
    if (!consultant?.id || !targetPackageId) return;
    setPreviewing(true);
    try {
      const q = new URLSearchParams({ target_package_id: targetPackageId });
      if (forceImmediate) q.set("force_immediate", "true");
      const r = await api.get(`/admin/consultants/${consultant.id}/billing/preview?${q.toString()}`);
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  }, [consultant?.id, targetPackageId, forceImmediate]);

  const runApply = useCallback(async () => {
    if (!consultant?.id || !targetPackageId) return;
    setApplying(true);
    try {
      const r = await api.post(`/admin/consultants/${consultant.id}/billing/change`, {
        target_package_id: targetPackageId,
        force_immediate: forceImmediate,
        note: "manual admin plan change",
      });
      toast.success(`Plan change ${r.data.proration?.action || "applied"} — audit #${(r.data.audit_id || "").slice(0, 8)}`);
      setConfirming(false);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  }, [consultant?.id, targetPackageId, forceImmediate, onOpenChange]);

  const loadAudit = useCallback(async () => {
    if (!consultant?.id) return;
    try {
      const r = await api.get(`/admin/consultants/${consultant.id}/billing/audit?limit=25`);
      setAudit(r.data);
    } catch {
      setAudit(null);
    }
  }, [consultant?.id]);

  useEffect(() => {
    if (open) {
      setPreview(null);
      setConfirming(false);
      setTargetPackageId("");
      setForceImmediate(false);
      loadAudit();
    }
  }, [open, loadAudit]);

  useEffect(() => {
    if (targetPackageId) runPreview();
  }, [targetPackageId, forceImmediate, runPreview]);

  const p = preview?.proration;
  const currentPkg = preview?.current_package;
  const showForcedDowngradeSwitch = p?.action === "downgrade";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="plan-manage-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-accent" />
            Manage Plan · {consultant?.name || consultant?.email}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Current plan:{" "}
            <span className="font-mono capitalize">
              {consultant?.plan || "starter"}
            </span>{" "}
            · billing:{" "}
            <span className="font-mono">
              {consultant?.plan_billing || "monthly"}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Target plan</Label>
              <Select value={targetPackageId} onValueChange={setTargetPackageId}>
                <SelectTrigger data-testid="plan-manage-target">
                  <SelectValue placeholder="Pick a package…" />
                </SelectTrigger>
                <SelectContent>
                  {packageOptions.map(([pid, meta]) => (
                    <SelectItem
                      key={pid} value={pid}
                      data-testid={`plan-manage-target-${pid}`}
                    >
                      {meta.label} — ${meta.amount}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {showForcedDowngradeSwitch && (
              <div className="flex flex-col justify-end">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={forceImmediate}
                    onCheckedChange={setForceImmediate}
                    data-testid="plan-manage-force"
                  />
                  <Label className="text-sm">
                    Force downgrade to apply immediately (credit issued)
                  </Label>
                </div>
              </div>
            )}
          </div>

          {previewing && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Computing proration…
            </div>
          )}

          {p && (
            <Card className="p-4 border border-accent/20" data-testid="plan-manage-preview">
              <div className="flex items-center justify-between gap-3">
                <div className="overline flex items-center gap-2">
                  <ChevronRight className="w-3.5 h-3.5" /> Billing preview
                </div>
                <span
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${actionChip(p.action)}`}
                >
                  {p.action}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <PreviewCell label="Current plan" value={currency(p.current_amount)} />
                <PreviewCell label="Target plan" value={currency(p.target_amount)} />
                <PreviewCell label="Unused credit" value={currency(p.unused_credit)} />
                <PreviewCell label="Prorated charge" value={currency(p.prorated_charge)} />
                <PreviewCell label="Unused days" value={`${p.unused_days} / ${p.cycle_days}`} />
                <PreviewCell label="Apply at" value={p.apply_at} />
                <PreviewCell
                  label="Effective at"
                  value={new Date(p.effective_at).toLocaleString()}
                />
                <PreviewCell
                  label="Net adjustment"
                  value={currency(p.net_adjustment)}
                  emphasis
                />
              </div>
              <p className="text-xs text-muted-foreground mt-3">{p.reason}</p>
            </Card>
          )}

          {audit && audit.audit?.length > 0 && (
            <Card className="p-4" data-testid="plan-manage-audit">
              <div className="overline flex items-center gap-2">
                <History className="w-3.5 h-3.5" /> Change history
              </div>
              <ul className="mt-3 space-y-2 text-sm">
                {audit.audit.slice(0, 5).map((a) => (
                  <li
                    key={a.id}
                    className="flex items-center justify-between gap-3"
                    data-testid={`plan-manage-audit-row-${a.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${actionChip(a.action)}`}
                      >
                        {a.action}
                      </span>
                      <span className="text-xs text-muted-foreground truncate">
                        {a.current_package_id || "—"} → {a.target_package_id}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs">{currency(a.net_adjustment)}</span>
                      <span className="text-[10px] text-muted-foreground uppercase tracking-widest">
                        {new Date(a.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {!confirming ? (
            <Button
              onClick={() => setConfirming(true)}
              disabled={!p || !targetPackageId}
              data-testid="plan-manage-review-btn"
            >
              Review & apply
            </Button>
          ) : (
            <Button
              onClick={runApply}
              disabled={applying}
              data-testid="plan-manage-confirm-btn"
              className="bg-red-500/20 border border-red-500/40 text-red-100 hover:bg-red-500/30"
            >
              {applying ? (
                <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Applying…</>
              ) : (
                <><ScrollText className="w-3.5 h-3.5 mr-1" /> Confirm & record audit</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewCell({ label, value, emphasis }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </div>
      <div className={emphasis ? "text-lg font-semibold mt-1" : "text-sm mt-1"}>
        {value}
      </div>
    </div>
  );
}
